// The new-experiment wizard (brief §4).
//
// Four steps, each answering one question, with the template supplying defaults for
// everything the user does not want to think about. The dose field is the one place
// the app deliberately offers no help: it is always empty, always the user's to fill
// from their own prescription or packaging.

import { useEffect, useState } from 'react';
import { api, type TemplateDetail, type TemplateSummary } from '../lib/api.ts';
import {
  Button, Card, ErrorNote, Field, Select, Spinner, TextArea, TextInput,
} from '../components/ui.tsx';
import { addDaysTo, longDate, todayLocal } from '../lib/format.ts';

interface InterventionDraft {
  name: string;
  type: string;
  dose: string;
  instructions: string;
  times: string[];
  example?: string;
}

export function Wizard({ navigate }: { navigate: (path: string) => void }) {
  const [step, setStep] = useState(0);
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [template, setTemplate] = useState<TemplateDetail | null>(null);

  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [reason, setReason] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [startDate, setStartDate] = useState(todayLocal());
  const [baselineDays, setBaselineDays] = useState(7);
  const [interventionDays, setInterventionDays] = useState(42);
  const [checkinTime, setCheckinTime] = useState('07:45');
  const [interventions, setInterventions] = useState<InterventionDraft[]>([]);

  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.experiments.templates().then((r) => setTemplates(r.templates)).catch((e) => setError((e as Error).message));
  }, []);

  const chooseTemplate = async (summary: TemplateSummary): Promise<void> => {
    try {
      const { template: full } = await api.experiments.template(summary.id);
      setTemplate(full);
      setName(full.id === 'blank' ? '' : full.name);
      setBaselineDays(full.suggested_baseline_days);
      setInterventionDays(full.suggested_duration_days);
      setInterventions(full.interventions.map((iv) => ({
        name: iv.name,
        type: iv.type,
        dose: '',                       // never pre-filled
        instructions: iv.instructions ?? '',
        times: iv.schedule.times.map((t) =>
          t.kind === 'clock' ? t.time : t.kind === 'window' ? t.from : '19:30'),
        ...(iv.example ? { example: iv.example } : {}),
      })));
      setStep(1);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const askAi = async (): Promise<void> => {
    setAiBusy(true);
    setError(null);
    try {
      const { hypothesis: result } = await api.ai.hypothesis({
        description: reason || goal || name,
        goal,
        interventions: interventions.map((i) => i.name),
      });
      setHypothesis(result.hypothesis);
      setAiWarnings(result.design_warnings);
      if (result.suggested_baseline_days) setBaselineDays(result.suggested_baseline_days);
      if (result.suggested_duration_days) setInterventionDays(result.suggested_duration_days);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAiBusy(false);
    }
  };

  const create = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.experiments.create({
        template_id: template?.id,
        name: name.trim(),
        goal: goal.trim(),
        reason: reason.trim(),
        hypothesis: hypothesis.trim(),
        start_date: startDate,
        baseline_days: baselineDays,
        intervention_days: interventionDays,
        checkin_time: checkinTime,
        interventions: interventions.map((iv) => ({
          name: iv.name,
          type: iv.type,
          instructions: iv.instructions || null,
          // Empty stays empty: an unfilled dose is recorded as "not specified",
          // never back-filled from the template.
          dose: iv.dose.trim() || null,
          schedule: {
            recurrence: 'daily',
            times: iv.times.map((t) => ({ kind: 'clock', time: t })),
          },
        })),
      }) as { experiment: { id: string } };

      // Materialise the first reminders immediately, so Today is right the moment
      // the user lands back on it rather than up to a minute later.
      await api.tick().catch(() => undefined);
      navigate(`/experiments/${created.experiment.id}`);
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  };

  if (!templates) return <Spinner />;

  const interventionStart = addDaysTo(startDate, baselineDays);
  const endDate = addDaysTo(interventionStart, interventionDays - 1);

  return (
    <div className="space-y-6">
      <StepDots step={step} total={4} />
      {error && <ErrorNote message={error} />}

      {step === 0 && (
        <section>
          <h2 className="text-lg font-semibold mb-1">What are you testing?</h2>
          <p className="text-sm text-muted mb-4">Pick a starting point. Everything stays editable.</p>
          <ul className="space-y-2">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => void chooseTemplate(t)}
                  className="card w-full text-left p-4 active:opacity-80"
                >
                  <p className="font-medium">{t.name}</p>
                  <p className="text-sm text-ink-2 mt-1 leading-snug">{t.description}</p>
                  {t.suggested_baseline_days > 0 && (
                    <p className="text-xs text-muted mt-2">
                      Suggested: {t.suggested_baseline_days}-day baseline, then {t.suggested_duration_days} days
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">What are you testing?</h2>

          <Field label="Name" required>
            <TextInput value={name} onChange={setName} placeholder="Upper airway / reflux trial" />
          </Field>

          <Field label="What do you want to change?" hint={template?.goal_examples.join(' · ')}>
            <TextInput value={goal} onChange={setGoal} placeholder="Reduce morning throat mucus" />
          </Field>

          <Field label="Why are you testing this?">
            <TextArea value={reason} onChange={setReason} rows={3}
              placeholder="What has been happening, and what you think might be causing it." />
          </Field>

          <Field label="Hypothesis" hint="What you expect to happen, and over what period.">
            <TextArea value={hypothesis} onChange={setHypothesis} rows={3}
              placeholder="If I use the spray consistently and cut late meals, morning mucus should fall over 4–6 weeks." />
          </Field>

          <Button disabled={aiBusy || (!reason && !goal && !name)} onClick={() => void askAi()}>
            {aiBusy ? 'Thinking…' : 'Help me word this'}
          </Button>

          {aiWarnings.length > 0 && (
            <Card className="bg-warning/10 border-warning/40">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1.5">Worth knowing</p>
              <ul className="text-sm space-y-1">
                {aiWarnings.map((w, i) => <li key={i}>· {w}</li>)}
              </ul>
            </Card>
          )}

          <StepNav onBack={() => setStep(0)} onNext={() => setStep(2)} nextDisabled={!name.trim()} />
        </section>
      )}

      {step === 2 && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">Baseline and duration</h2>
          <p className="text-sm text-ink-2">
            A baseline records the same measures before anything changes. Without one there is
            nothing to compare against — it is optional, but it is the whole point.
          </p>

          <Field label="Start date">
            <TextInput type="date" value={startDate} onChange={setStartDate} />
          </Field>

          <Field label="Baseline" hint="Set to 0 to start treatment straight away.">
            <Select
              value={String(baselineDays)}
              onChange={(v) => setBaselineDays(Number(v))}
              options={[0, 3, 7, 14].map((d) => ({ value: String(d), label: d === 0 ? 'No baseline' : `${d} days` }))}
            />
          </Field>

          <Field label="How long will you run the treatment?">
            <Select
              value={String(interventionDays)}
              onChange={(v) => setInterventionDays(Number(v))}
              options={[14, 21, 28, 42, 56, 84].map((d) => ({ value: String(d), label: `${d} days (${Math.round(d / 7)} weeks)` }))}
            />
          </Field>

          <Field label="Daily check-in time">
            <TextInput type="time" value={checkinTime} onChange={setCheckinTime} />
          </Field>

          <Card className="text-sm space-y-1">
            {baselineDays > 0 && (
              <p><span className="text-muted">Baseline:</span> {longDate(startDate)} – {longDate(addDaysTo(startDate, baselineDays - 1))}</p>
            )}
            <p><span className="text-muted">Treatment:</span> {longDate(interventionStart)} – {longDate(endDate)}</p>
          </Card>

          <StepNav onBack={() => setStep(1)} onNext={() => setStep(3)} />
        </section>
      )}

      {step === 3 && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">What will you do?</h2>

          {interventions.length === 0 && (
            <p className="text-sm text-ink-2">
              No actions yet. You can add them after creating the experiment.
            </p>
          )}

          {interventions.map((iv, index) => (
            <Card key={index} className="space-y-3">
              <Field label="Action">
                <TextInput
                  value={iv.name}
                  onChange={(v) => setInterventions((prev) => prev.map((x, i) => i === index ? { ...x, name: v } : x))}
                  placeholder={iv.example ?? 'What you will do'}
                />
              </Field>

              {/* Empty by default, and stays empty unless the user types. The app
                  must never suggest a dose (brief §10, §14, §55). */}
              <Field
                label="Dose / amount"
                hint="Copy this from your prescription, the packaging, or what your clinician told you. HealthLab will never fill this in for you."
              >
                <TextInput
                  value={iv.dose}
                  onChange={(v) => setInterventions((prev) => prev.map((x, i) => i === index ? { ...x, dose: v } : x))}
                  placeholder="e.g. as prescribed"
                />
              </Field>

              <Field label="Reminder times">
                <div className="flex flex-wrap gap-2">
                  {iv.times.map((time, ti) => (
                    <input
                      key={ti}
                      type="time"
                      value={time}
                      onChange={(e) => setInterventions((prev) => prev.map((x, i) =>
                        i === index ? { ...x, times: x.times.map((t, j) => j === ti ? e.target.value : t) } : x))}
                      className="min-h-11 px-3 rounded-xl bg-surface border border-hairline"
                    />
                  ))}
                  <Button
                    onClick={() => setInterventions((prev) => prev.map((x, i) =>
                      i === index ? { ...x, times: [...x.times, '20:00'] } : x))}
                  >
                    + Time
                  </Button>
                </div>
              </Field>

              {iv.instructions && <p className="text-xs text-muted">{iv.instructions}</p>}
            </Card>
          ))}

          <Button
            onClick={() => setInterventions((prev) => [...prev, {
              name: '', type: 'other', dose: '', instructions: '', times: ['09:00'],
            }])}
          >
            + Add another action
          </Button>

          {interventions.length > 1 && (
            <Card className="bg-warning/10 border-warning/40">
              <p className="text-sm">
                You are changing {interventions.length} things at once. This can show whether the
                combination helps, but not which part of it did.
              </p>
            </Card>
          )}

          <div className="flex gap-2 pt-2">
            <Button className="flex-1" onClick={() => setStep(2)}>Back</Button>
            <Button variant="primary" className="flex-1" disabled={saving} onClick={() => void create()}>
              {saving ? 'Creating…' : 'Start experiment'}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}

function StepDots({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex gap-1.5" aria-label={`Step ${step + 1} of ${total}`}>
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-accent' : 'bg-hairline'}`} />
      ))}
    </div>
  );
}

function StepNav({ onBack, onNext, nextDisabled }: { onBack: () => void; onNext: () => void; nextDisabled?: boolean }) {
  return (
    <div className="flex gap-2 pt-2">
      <Button className="flex-1" onClick={onBack}>Back</Button>
      <Button variant="primary" className="flex-1" disabled={nextDisabled} onClick={onNext}>Next</Button>
    </div>
  );
}
